import React from "react";

interface ModeRendererProps {
  brand: string;
  today: string;
  Copy: string;
}

export function VasarelyAtosMode({ brand }: ModeRendererProps) {
  const width = 794;
  const height = 1123;

  // Atos quantum gradient - neon blue with binary depth
  const bgGradient = (
    <defs>
      <linearGradient id="atosGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: "#0A0A0A", stopOpacity: 1 }} />
        <stop offset="50%" style={{ stopColor: "#001F3F", stopOpacity: 1 }} />
        <stop offset="100%" style={{ stopColor: "#00FFFF", stopOpacity: 0.2 }} />
      </linearGradient>
      <radialGradient id="quantumGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" style={{ stopColor: "#00FFFF", stopOpacity: 0.6 }} />
        <stop offset="100%" style={{ stopColor: "#00FFFF", stopOpacity: 0 }} />
      </radialGradient>
    </defs>
  );

  // Processor tessellation - chip circuit patterns
  const processorTiles: JSX.Element[] = [];
  const tilesX = 18;
  const tilesY = 24;
  const tileSize = 40;
  
  for (let i = 0; i < tilesX; i++) {
    for (let j = 0; j < tilesY; j++) {
      const x = -360 + i * tileSize;
      const y = -480 + j * tileSize;
      const isActive = Math.random() > 0.6;
      
      processorTiles.push(
        <g key={`tile-${i}-${j}`}>
          <rect
            x={x}
            y={y}
            width={tileSize}
            height={tileSize}
            fill="none"
            stroke={isActive ? "#00FFFF" : "#003D7A"}
            strokeWidth={isActive ? 2 : 1}
            opacity={isActive ? 0.4 : 0.15}
          />
          {isActive && (
            <circle
              cx={x + tileSize / 2}
              cy={y + tileSize / 2}
              r={8}
              fill="#00FFFF"
              opacity={0.5}
            />
          )}
        </g>
      );
    }
  }

  // Binary motifs - 0 and 1 patterns
  const binaryPattern: JSX.Element[] = [];
  const binaryGrid = 24;
  const binarySpacing = 30;
  
  for (let i = 0; i < binaryGrid; i++) {
    for (let j = 0; j < binaryGrid; j++) {
      const x = -360 + i * binarySpacing;
      const y = -360 + j * binarySpacing;
      const bit = Math.random() > 0.5 ? "1" : "0";
      
      binaryPattern.push(
        <text
          key={`binary-${i}-${j}`}
          x={x}
          y={y}
          fill="#00FFFF"
          fontSize={12}
          fontFamily="monospace"
          opacity={0.15}
          textAnchor="middle"
        >
          {bit}
        </text>
      );
    }
  }

  // Neon blue lines - quantum computing pathways
  const quantumLines: JSX.Element[] = [];
  for (let line = 0; line < 32; line++) {
    const isVertical = line % 2 === 0;
    const pos = -360 + (line % 16) * 45;
    
    if (isVertical) {
      quantumLines.push(
        <line
          key={`quantum-v-${line}`}
          x1={pos}
          y1={-500}
          x2={pos}
          y2={500}
          stroke="#00FFFF"
          strokeWidth={line % 4 === 0 ? 2 : 1}
          opacity={line % 4 === 0 ? 0.3 : 0.12}
          strokeDasharray="10,5"
        />
      );
    } else {
      quantumLines.push(
        <line
          key={`quantum-h-${line}`}
          x1={-360}
          y1={pos}
          x2={360}
          y2={pos}
          stroke="#00FFFF"
          strokeWidth={line % 4 === 0 ? 2 : 1}
          opacity={line % 4 === 0 ? 0.3 : 0.12}
          strokeDasharray="10,5"
        />
      );
    }
  }

  // High-performance computing nodes - quantum qubits
  const quantumNodes: JSX.Element[] = [];
  const nodeRings = [100, 170, 240, 310];
  
  nodeRings.forEach((nodeR, ri) => {
    const nodeCount = 8 + ri * 4;
    for (let n = 0; n < nodeCount; n++) {
      const angle = (n / nodeCount) * 2 * Math.PI;
      const nx = Math.cos(angle) * nodeR;
      const ny = Math.sin(angle) * nodeR;
      
      quantumNodes.push(
        <g key={`node-${ri}-${n}`}>
          <circle
            cx={nx}
            cy={ny}
            r={28 - ri * 4}
            fill="url(#quantumGlow)"
          />
          <circle
            cx={nx}
            cy={ny}
            r={18 - ri * 3}
            fill="none"
            stroke="#00FFFF"
            strokeWidth={2}
            opacity={0.6}
          />
          <circle
            cx={nx}
            cy={ny}
            r={10 - ri * 1.5}
            fill="#00FFFF"
            opacity={0.4}
          />
        </g>
      );
    }
  });

  // Security blueprint layers - encrypted architecture
  const securityBlueprint: JSX.Element[] = [];
  for (let layer = 0; layer < 16; layer++) {
    const radius = 60 + layer * 22;
    const opacity = 0.25 - layer * 0.012;
    
    securityBlueprint.push(
      <circle
        key={`blueprint-${layer}`}
        cx={0}
        cy={0}
        r={radius}
        fill="none"
        stroke="#FFFFFF"
        strokeWidth={layer % 4 === 0 ? 2 : 1}
        opacity={opacity}
      />
    );
  }

  // Data center grid - server rack visualization
  const datacenterGrid: JSX.Element[] = [];
  const rackRows = 10;
  const rackCols = 16;
  const rackW = 42;
  const rackH = 90;
  
  for (let row = 0; row < rackRows; row++) {
    for (let col = 0; col < rackCols; col++) {
      const x = -340 + col * rackW;
      const y = -450 + row * rackH;
      const isActive = Math.random() > 0.5;
      
      datacenterGrid.push(
        <rect
          key={`rack-${row}-${col}`}
          x={x}
          y={y}
          width={rackW - 4}
          height={rackH - 4}
          fill="none"
          stroke={isActive ? "#00FFFF" : "#003D7A"}
          strokeWidth={1}
          opacity={isActive ? 0.3 : 0.1}
          rx={4}
        />
      );
    }
  }

  // Central quantum core
  const quantumCore = (
    <g>
      <circle cx={0} cy={0} r={100} fill="url(#quantumGlow)" />
      <circle cx={0} cy={0} r={60} fill="none" stroke="#00FFFF" strokeWidth={4} opacity={0.7} />
      <circle cx={0} cy={0} r={45} fill="none" stroke="#FFFFFF" strokeWidth={3} opacity={0.5} />
      <circle cx={0} cy={0} r={30} fill="none" stroke="#00FFFF" strokeWidth={2.5} opacity={0.6} />
      <circle cx={0} cy={0} r={15} fill="#00FFFF" opacity={0.5} />
    </g>
  );

  return (
    <svg width={width} height={height} xmlns="http://www.w3.org/2000/svg">
      {bgGradient}
      <rect width={width} height={height} fill="url(#atosGradient)" />
      <g transform={`translate(${width / 2}, ${height / 2})`}>
        {datacenterGrid}
        {binaryPattern}
        {processorTiles}
        {quantumLines}
        {securityBlueprint}
        {quantumNodes}
        {quantumCore}
      </g>
    </svg>
  );
}
